import { committedProduct, marketDetail, marketsWithCommitment } from './marketReportsV1';

/**
 * Headline figures for the delivery-report panel over one window — four questions, in the
 * order a customer asks them: Commitment (what did we sell), Backed (was the sale covered by
 * reachable capacity), Activated (how often did the grid actually call the service), and
 * Responded (of the activated periods, in how many did the battery follow its setpoint).
 * Every rate is unit-free.
 *
 * @typedef {object} DeliverySummary
 * @property {number} meanCommittedMw mean `committedPower` over the reports carrying a market report
 * @property {{label: string, color: string}|null} committedProduct
 * @property {number} marketReportCount reports carrying a market report at all
 * @property {number} committedCount non-zero commitment and not unmeasured — the Backed denominator
 * @property {number} backedCount of `committedCount`, periods whose **minimum** availability covered the commitment
 * @property {number} committedOutOfServiceCount of `committedCount`, settled with every member disabled
 * @property {number} committedUnmeasuredCount committed periods the writer could not measure — outside `committedCount`
 * @property {number|null} backedRate `backedCount / committedCount`, null when nothing to ask
 * @property {number} activatedCount measured periods in which the FCR service was activated —
 *   any committed market's `fcrActivatedDuration > 0`, or `isCalled` on rows from before the
 *   per-market figures
 * @property {number} respondedCount of `activatedCount`, periods whose setpoint following
 *   score exceeds `RESPONDED_SCORE_THRESHOLD`
 * @property {number|null} respondedRate `respondedCount / activatedCount`, null when never activated
 * @property {number} measuredCount
 * @property {number} outOfServiceCount
 * @property {number} unmeasuredCount
 * @property {number} reportCount
 * @property {number} firstEnd ms
 * @property {number} lastEnd ms
 */

/**
 * The setpoint-following score an activated period must exceed to count as responded. Prod
 * scores on an activated period sit around 0.96 and the distribution is bimodal with nothing
 * between roughly 0.5 and 0.86, so any cutoff in that range gives the same verdict.
 */
export const RESPONDED_SCORE_THRESHOLD = 0.8;

/**
 * Was the battery called in this period: measured, with a non-zero active duration. Kept only
 * as the fallback activation notion for rows settled before the per-market
 * `fcrActivatedDuration` existed — see `isFcrActivated`. The writer computes a genuine
 * setpoint following score for every measured period (a quiet period holding its zero
 * setpoint scores high, not zero), so the score is never gated on this test.
 */
export const isCalled = r => r.measurement === 'measured' && r.activeDuration > 0;

/**
 * Did this period's setpoint following score exceed the responded bar. Only meaningful on an
 * activated period: Responded is defined as the share of activated periods clearing this.
 */
export const isResponded = r => r.setpointFollowingScore > RESPONDED_SCORE_THRESHOLD;

/**
 * Was the FCR service activated in this period: the grid frequency left a committed market's
 * dead band, per the settled per-market `fcrActivatedDuration`. Rows from before the
 * per-market figures fall back to the battery-wide `isCalled` notion (measured with a
 * non-zero active duration). Never true for a non-measured period.
 *
 * @param {import('./marketReportsV1').Delivered} r
 * @param {Array<{key: string}>} committedMarkets `marketsWithCommitment` over the window
 */
export const isFcrActivated = (r, committedMarkets) => {
    if (r.measurement !== 'measured') {
        return false;
    }
    const durations = committedMarkets
        .map(market => marketDetail(r, market)?.fcrActivatedDuration)
        .filter(seconds => seconds !== null && seconds !== undefined);
    if (durations.length === 0) {
        return isCalled(r);
    }
    return durations.some(seconds => seconds > 0);
};

/**
 * Reduce settled delivery reports to the panel's headline figures. Null for an empty window.
 *
 * @param {import('./marketReportsV1').Delivered[]} reports
 * @returns {DeliverySummary|null}
 */
export const summarizeDelivery = reports => {
    if (reports.length === 0) {
        return null;
    }

    // A report whose `markets` is null carried no market report at all — a legacy row is an
    // absence, not a battery that committed nothing, and averaging it in as a zero would drag
    // the commitment toward zero in proportion to how much of the window predates the
    // measurement.
    const withMarkets = reports.filter(r => r.markets !== null);
    const marketReportCount = withMarkets.length;
    const meanCommittedMw = marketReportCount === 0 ? 0 : withMarkets.reduce((a, r) => a + r.markets.committedPower, 0) / marketReportCount;

    const committedAll = withMarkets.filter(r => r.markets.committedPower !== 0);
    // An unmeasured period is one the writer could see no member for — a monitoring outage,
    // not a delivery. Left in the denominator it reads as "sold and not backed", which is
    // exactly what a fleet-wide outage produced: three days of every battery at 0 % backed
    // while nothing was wrong at the sites. Excluded and counted separately.
    const committedUnmeasured = committedAll.filter(r => r.measurement === 'unmeasured');
    const committed = committedAll.filter(r => r.measurement !== 'unmeasured');
    // Periods with every member disabled are deliberately kept: zero availability against a
    // live commitment is a real claim — the schedule kept selling capacity nothing backed.
    const committedOutOfService = committed.filter(r => r.measurement === 'out_of_service');
    // Backed is tested on the **minimum**, not the mean: a capacity commitment is an
    // obligation to be ready throughout the period, and the mean forgives a real dropout on an
    // over-provisioned battery. A report with no availability triple is not backed.
    const backed = committed.filter(r => r.markets.availablePowerMin !== null && r.markets.availablePowerMin >= r.markets.committedPower);
    const backedRate = committed.length > 0 ? backed.length / committed.length : null;

    const measured = reports.filter(r => r.measurement === 'measured');
    // Activated is per committed market where the wire carries the figure, battery-wide
    // `isCalled` on rows from before it — see `isFcrActivated`.
    const committedMarkets = marketsWithCommitment(reports);
    const activated = measured.filter(r => isFcrActivated(r, committedMarkets));
    // Responded is a share of periods, not a mean of scores: of the activated periods, how
    // many cleared the score bar.
    const responded = activated.filter(isResponded);

    const ends = reports.map(r => Date.parse(r.end));
    return {
        meanCommittedMw,
        committedProduct: committedProduct(reports),
        marketReportCount,
        committedCount: committed.length,
        backedCount: backed.length,
        committedOutOfServiceCount: committedOutOfService.length,
        committedUnmeasuredCount: committedUnmeasured.length,
        backedRate,
        activatedCount: activated.length,
        respondedCount: responded.length,
        respondedRate: activated.length > 0 ? responded.length / activated.length : null,
        measuredCount: measured.length,
        outOfServiceCount: reports.filter(r => r.measurement === 'out_of_service').length,
        unmeasuredCount: reports.filter(r => r.measurement === 'unmeasured').length,
        reportCount: reports.length,
        firstEnd: Math.min(...ends),
        lastEnd: Math.max(...ends),
    };
};
